import { useRef, useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import { UploadCloud, X, File as FileIcon } from 'lucide-react';
import { formatBytes } from '../../lib/utils';

const mainVariant = {
  initial: { x: 0, y: 0 },
  animate: { x: 20, y: -20, opacity: 0.9 },
};
const secondaryVariant = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
};

/**
 * File Upload（Aceternity UI 风格）— 网格背景 + 拖拽 + 流动光路
 * onChange 返回新增文件列表。
 */
export function FileUpload({ onChange, hint }: { onChange?: (files: File[]) => void; hint?: string }) {
  const [files, setFiles] = useState<File[]>([]);
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const addFiles = useCallback(
    (list: FileList | null) => {
      if (!list?.length) return;
      const next = [...files, ...Array.from(list)];
      setFiles(next);
      onChange?.(Array.from(list));
    },
    [files, onChange]
  );

  return (
    <div className="w-full">
      <div
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => { e.preventDefault(); setDragging(false); addFiles(e.dataTransfer.files); }}
        className="group/file relative block w-full cursor-pointer"
      >
        <input
          ref={inputRef}
          type="file"
          multiple
          className="hidden"
          onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }}
        />

        {/* 网格背景 */}
        <div className="absolute inset-0 rounded-2xl [background-size:24px_24px] [background-image:linear-gradient(to_right,rgba(255,60,80,0.07)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,60,80,0.07)_1px,transparent_1px)]" />

        {/* 外圈主框 */}
        <motion.div
          whileHover="animate"
          className={`relative h-44 overflow-hidden rounded-2xl bg-card/40 border-2 border-dashed transition-colors ${dragging ? 'border-primary bg-primary/10' : 'border-border-soft'}`}
        >
          {/* 中央内容 */}
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-center px-4">
            <motion.span
              animate={dragging ? { scale: 1.15, y: -4 } : { scale: 1, y: 0 }}
              className="flex items-center justify-center w-14 h-14 rounded-2xl brand-gradient glow-primary"
            >
              <UploadCloud className="w-7 h-7 text-white" />
            </motion.span>
            <div>
              <p className="text-base font-bold">
                上传 <span className="neon-text">文件</span>
              </p>
              <p className="text-xs text-text-muted mt-0.5">{hint || '拖拽到此处，或点击选择文件'}</p>
            </div>
          </div>

          {/* 流动光路（Aceternity 特征） */}
          <motion.div variants={mainVariant} transition={{ duration: 12, repeat: Infinity, ease: 'linear' }} className="absolute -bottom-2 -left-2 w-64 h-44 opacity-70">
            <svg className="w-full h-full" viewBox="0 0 400 120" fill="none">
              <defs>
                <linearGradient id="fu-grad" x1="0" y1="50%" x2="100%" y2="50%">
                  <stop offset="0%" stopColor="#e11d2e" stopOpacity="0" />
                  <stop offset="50%" stopColor="#ff5c75" stopOpacity="1" />
                  <stop offset="100%" stopColor="#e11d2e" stopOpacity="0" />
                </linearGradient>
              </defs>
              <rect x="40" y="40" width="360" height="60" rx="10" stroke="url(#fu-grad)" strokeWidth="1.5" />
              <rect x="20" y="20" width="380" height="80" rx="14" stroke="url(#fu-grad)" strokeWidth="1.5" strokeOpacity="0.5" />
              <path d="M40 40 H400 V100" stroke="url(#fu-grad)" strokeWidth="1.5" />
            </svg>
          </motion.div>
          <motion.div variants={secondaryVariant} transition={{ duration: 12, repeat: Infinity, ease: 'linear', delay: 2 }} className="absolute -top-2 -right-2 w-56 h-40 opacity-50">
            <svg className="w-full h-full" viewBox="0 0 400 120" fill="none">
              <rect x="60" y="30" width="320" height="60" rx="10" stroke="url(#fu-grad)" strokeWidth="1.5" />
              <path d="M60 30 H380 V90" stroke="url(#fu-grad)" strokeWidth="1.5" />
            </svg>
          </motion.div>
        </motion.div>
      </div>

      {/* 已选文件列表 */}
      {files.length > 0 && (
        <div className="mt-3 space-y-2">
          {files.map((f, i) => (
            <motion.div
              key={f.name + i}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex items-center gap-2 px-3 py-2 rounded-xl bg-card/60 border border-border-soft text-sm"
            >
              <FileIcon className="w-4 h-4 text-primary flex-shrink-0" />
              <span className="flex-1 min-w-0 truncate">{f.name}</span>
              <span className="text-xs text-text-muted flex-shrink-0">{formatBytes(f.size)}</span>
              <button
                onClick={() => setFiles((prev) => prev.filter((_, idx) => idx !== i))}
                className="p-1 rounded-lg hover:bg-card text-text-muted hover:text-text transition-colors"
                aria-label="移除"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}
